import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Image as ImageIcon,
  ImagePlus,
  Inbox,
  Link as LinkIcon,
  Loader2,
  Maximize2,
  MessageCircle,
  Mic,
  MoreHorizontal,
  Package,
  Paperclip,
  PauseCircle,
  RefreshCw,
  Search,
  Send,
  ShoppingBag,
  Video,
  Wifi,
  WifiOff,
  X,
} from 'lucide-react';
import { ChatAccountOption, ChatMessage, ChatSession } from '../types';
import {
  connectChatEvents,
  getChatMessages,
  getChatSessions,
  markChatRead,
  sendChatImageMessage,
  sendChatMessage,
} from '../services/api';
import { showToast } from '../utils/toast';
import Select from './Select';

type SessionFilter = 'all' | 'unread' | 'paused' | 'online';

type PendingAttachment = {
  file: File;
  previewUrl: string;
};

const SESSION_LIST_MIN_WIDTH = 280;
const SESSION_LIST_MAX_WIDTH = 460;
const SESSION_LIST_DEFAULT_WIDTH = 348;
const MESSAGE_AREA_MIN_WIDTH = 560;
const CONTEXT_COLLAPSED_WIDTH = 48;
const CONTEXT_PANEL_WIDTH = 306;

const getSessionKey = (session: Pick<ChatSession, 'cookie_id' | 'chat_id'>) => `${session.cookie_id}::${session.chat_id}`;

const resolveAssetUrl = (value?: unknown): string => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('//')) return `https:${trimmed}`;
  if (
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('data:') ||
    trimmed.startsWith('blob:') ||
    trimmed.startsWith('/')
  ) {
    return trimmed;
  }
  return `/${trimmed.replace(/^\/+/, '')}`;
};

const normalizeExternalUrl = (value?: unknown): string => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  const match = trimmed.match(/https?:\/\/[^\s"'<>]+|\/\/[^\s"'<>]+|xianyu:\/\/[^\s"'<>]+/i);
  if (!match) return '';
  const url = match[0];
  if (url.startsWith('//')) return `https:${url}`;
  if (/^(https?:|xianyu:)\/\//i.test(url)) return url;
  return '';
};

const formatItemPrice = (price?: string | number | null) => {
  const value = String(price ?? '').trim();
  if (!value) return '';
  return `¥${value.replace(/^[¥￥\s]+/, '')}`;
};

const formatTime = (value?: string) => {
  if (!value) return '';
  const date = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return value;

  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) {
    return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' });
};

const formatFullTime = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatPause = (seconds?: number) => {
  if (!seconds || seconds <= 0) return '';
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  if (minutes <= 0) return `${rest}秒`;
  return `${minutes}分${rest.toString().padStart(2, '0')}秒`;
};

const toTimestamp = (value?: string | null) => {
  if (!value) return 0;
  const date = new Date(value.replace(' ', 'T'));
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const getPauseRemaining = (session?: Pick<ChatSession, 'auto_reply_paused_until' | 'auto_reply_remaining_seconds'>, now = Date.now()) => {
  if (!session) return 0;
  const pausedUntil = toTimestamp(session.auto_reply_paused_until);
  if (pausedUntil > 0) {
    return Math.max(0, Math.ceil((pausedUntil - now) / 1000));
  }
  return Math.max(0, Number(session.auto_reply_remaining_seconds || 0));
};

const sourceLabel = (source?: string) => {
  const labels: Record<string, string> = {
    buyer: '买家',
    manual: '人工',
    manual_external: '人工',
    keyword: '关键词',
    ai: 'AI',
    default: '默认',
    api: 'API',
    delivery: '自动发货',
  };
  return labels[source || ''] || source || '';
};

const getMessagePreview = (session: ChatSession) => {
  if (session.last_message) return session.last_message;
  const labels: Record<string, string> = {
    image: '[图片]',
    link: '[链接]',
    order_card: '[订单卡片]',
    item_card: '[商品卡片]',
    card: '[卡片消息]',
  };
  return labels[session.last_message_type] || '[消息]';
};

const isSystemDisplayName = (value?: string | null) => {
  const name = String(value || '').trim();
  if (!name) return false;
  const normalized = name.replace(/^[\[【(（\s]+|[\]】)）\s]+$/g, '');
  const exact = new Set([
    '等待你发货',
    '等待买家付款',
    '等待卖家发货',
    '买家已拍下，待付款',
    '买家消息',
    '发来一条消息',
    '发来一条新消息',
    '快给ta一个评价吧~',
    '快给ta一个评价吧～',
    '卖家人不错？送Ta闲鱼小红花',
  ]);
  if (exact.has(name) || exact.has(normalized)) return true;
  return [
    '我已付款',
    '已付款',
    '待发货',
    '待付款',
    '已拍下',
    '你已发货',
    '已发货',
    '交易关闭',
    '交易成功',
    '确认收货',
    '退款成功',
    '评价吧',
    '温馨提醒',
  ].some(keyword => name.includes(keyword));
};

const getBuyerDisplayName = (session?: Pick<ChatSession, 'buyer_name' | 'buyer_id'> | null) => {
  const buyerName = String(session?.buyer_name || '').trim();
  if (buyerName && !isSystemDisplayName(buyerName)) return buyerName;
  return String(session?.buyer_id || '').trim() || '未知买家';
};

const getMessageBuyerDisplayName = (message: ChatMessage, session: ChatSession) => {
  const messageBuyerName = String(message.buyer_name || '').trim();
  if (messageBuyerName && !isSystemDisplayName(messageBuyerName)) return messageBuyerName;
  return getBuyerDisplayName(session);
};

const getInitials = (name?: string) => {
  const cleaned = (name || '').trim();
  if (!cleaned) return '闲';
  return cleaned.slice(0, 1).toUpperCase();
};

const getImageFromMessage = (message: ChatMessage) => {
  const metadata = message.metadata || {};
  return resolveAssetUrl(metadata.image_url || metadata.local_image_url || metadata.url);
};

const Avatar: React.FC<{
  src?: string;
  name?: string;
  className?: string;
  textClassName?: string;
}> = ({ src, name, className = 'h-10 w-10 rounded-2xl', textClassName = 'text-sm' }) => {
  const [failed, setFailed] = useState(false);
  const imageUrl = resolveAssetUrl(src);

  useEffect(() => {
    setFailed(false);
  }, [imageUrl]);

  return (
    <div className={`flex flex-none items-center justify-center overflow-hidden bg-slate-200 text-slate-700 ${className}`}>
      {imageUrl && !failed ? (
        <img
          src={imageUrl}
          alt={name || '头像'}
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <span className={`font-bold ${textClassName}`}>{getInitials(name)}</span>
      )}
    </div>
  );
};

const MessageBody: React.FC<{
  message: ChatMessage;
  onImageClick: (url: string, alt?: string) => void;
  onImageLoad?: () => void;
}> = ({ message, onImageClick, onImageLoad }) => {
  const metadata = message.metadata || {};
  const imageUrl = getImageFromMessage(message);
  const contentLinkUrl = normalizeExternalUrl(message.content);
  const metadataLinkUrl = normalizeExternalUrl(metadata.url);
  const linkUrl = contentLinkUrl || (!message.content?.trim() ? metadataLinkUrl : '');
  const title = metadata.title || metadata.item_title || metadata.order_id || metadata.url;
  const summary = metadata.summary || metadata.desc || metadata.order_id || metadata.item_id || message.content;

  if (message.message_type === 'image') {
    return (
      <div className="space-y-2">
        {imageUrl ? (
          <button
            type="button"
            onClick={() => onImageClick(imageUrl, message.content || '聊天图片')}
            className="group relative block max-w-full overflow-hidden rounded-2xl text-left outline-none focus-visible:ring-2 focus-visible:ring-[#E4C400]"
          >
            <img
              src={imageUrl}
              alt="聊天图片"
              onLoad={onImageLoad}
              className="max-h-72 max-w-full rounded-2xl border border-slate-200 object-cover"
            />
            <span className="absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-full bg-slate-700/70 text-white opacity-0 transition group-hover:opacity-100">
              <Maximize2 className="h-4 w-4" />
            </span>
          </button>
        ) : (
          <div className="flex items-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-500">
            <ImageIcon className="h-4 w-4" />
            图片消息
          </div>
        )}
        {message.content && message.content !== '[图片]' && <p className="text-sm leading-6">{message.content}</p>}
      </div>
    );
  }

  if (message.message_type === 'link' && linkUrl) {
    return (
      <a
        href={linkUrl}
        target="_blank"
        rel="noreferrer"
        className="flex max-w-sm items-start gap-3 rounded-2xl border border-blue-100 bg-blue-50 px-3 py-3 text-left text-blue-700 transition hover:border-blue-200 hover:bg-blue-100"
      >
        <LinkIcon className="mt-0.5 h-4 w-4 flex-none" />
        <span className="min-w-0 break-words text-sm leading-5">{message.content || linkUrl}</span>
      </a>
    );
  }

  if (message.message_type === 'order_card' || message.message_type === 'item_card' || message.message_type === 'card') {
    const Icon = message.message_type === 'order_card' ? ShoppingBag : Package;
    const label = message.message_type === 'order_card' ? '订单卡片' : message.message_type === 'item_card' ? '商品卡片' : '卡片消息';
    const cardUrl = normalizeExternalUrl(metadata.url);

    return (
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white px-3 py-3 text-slate-900">
        <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-slate-500">
          <Icon className="h-4 w-4" />
          <span>{label}</span>
        </div>
        <div className="line-clamp-2 text-sm font-semibold">{title || message.content || label}</div>
        {summary && <div className="mt-1 line-clamp-2 text-xs leading-5 text-slate-500">{summary}</div>}
        {cardUrl && (
          <a href={cardUrl} target="_blank" rel="noreferrer" className="mt-2 inline-flex text-xs font-semibold text-blue-600">
            打开链接
          </a>
        )}
      </div>
    );
  }

  return <div className="whitespace-pre-wrap break-words text-sm leading-6">{message.content}</div>;
};

const ProductDetailModal: React.FC<{
  session: ChatSession;
  onClose: () => void;
}> = ({ session, onClose }) => {
  const productImage = resolveAssetUrl(session.item_image);
  const pauseRemaining = getPauseRemaining(session);
  const buyerDisplayName = getBuyerDisplayName(session);

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-700/35 px-4 py-8">
      <button type="button" className="absolute inset-0 cursor-default" aria-label="关闭商品详情" onClick={onClose} />
      <div className="relative flex max-h-[86vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900">商品详情</div>
            <div className="mt-1 truncate text-xs font-medium text-slate-500">{session.item_id || '暂无商品 ID'}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-950"
            title="关闭"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid min-h-0 gap-5 overflow-y-auto p-5 md:grid-cols-[240px_minmax(0,1fr)]">
          <div className="aspect-square overflow-hidden rounded-2xl bg-slate-100">
            {productImage ? (
              <img src={productImage} alt={session.item_title || '商品图片'} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full flex-col items-center justify-center text-slate-400">
                <Package className="mb-2 h-10 w-10" />
                <span className="text-xs font-semibold">暂无商品图</span>
              </div>
            )}
          </div>

          <div className="min-w-0">
            <h3 className="text-lg font-semibold leading-7 text-slate-900">{session.item_title || '未获取到商品标题'}</h3>
            {session.item_price && <div className="mt-3 text-2xl font-semibold text-slate-900">{formatItemPrice(session.item_price)}</div>}

            <div className="mt-5 divide-y divide-slate-100 rounded-2xl border border-slate-200">
              {[
                ['买家', buyerDisplayName || '-'],
                ['会话', session.chat_id || '-'],
                ['账号', session.account_name || session.cookie_id || '-'],
                ['最近消息', formatFullTime(session.last_message_at)],
                ['自动回复', pauseRemaining > 0 ? `暂停中 ${formatPause(pauseRemaining)}` : '运行中'],
              ].map(([label, value]) => (
                <div key={label} className="grid grid-cols-[88px_minmax(0,1fr)] gap-3 px-4 py-3 text-sm">
                  <span className="font-semibold text-slate-500">{label}</span>
                  <span className="min-w-0 break-words font-medium text-slate-900">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ImageLightbox: React.FC<{
  image: { url: string; alt?: string };
  onClose: () => void;
}> = ({ image, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-800/75 p-4">
    <button type="button" className="absolute inset-0 cursor-zoom-out" aria-label="关闭图片预览" onClick={onClose} />
    <div className="relative max-h-full max-w-5xl">
      <button
        type="button"
        onClick={onClose}
        className="absolute -right-2 -top-2 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white text-slate-700 shadow-lg transition hover:bg-slate-100 hover:text-slate-950"
        title="关闭"
      >
        <X className="h-5 w-5" />
      </button>
      <img
        src={image.url}
        alt={image.alt || '聊天图片'}
        className="max-h-[88vh] max-w-full rounded-2xl object-contain shadow-2xl"
      />
    </div>
  </div>
);

interface MessageCenterProps {
  targetSessionKey?: string;
  onUnreadChange?: () => void;
}

const MessageCenter: React.FC<MessageCenterProps> = ({ targetSessionKey = '', onUnreadChange }) => {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [accounts, setAccounts] = useState<ChatAccountOption[]>([]);
  const [selectedKey, setSelectedKey] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [cookieFilter, setCookieFilter] = useState('');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<SessionFilter>('all');
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState('');
  const [attachment, setAttachment] = useState<PendingAttachment | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [contextCollapsed, setContextCollapsed] = useState(true);
  const [sessionListWidth, setSessionListWidth] = useState(SESSION_LIST_DEFAULT_WIDTH);
  const [resizingSessionList, setResizingSessionList] = useState(false);
  const [pauseNow, setPauseNow] = useState(Date.now());
  const [lightbox, setLightbox] = useState<{ url: string; alt?: string } | null>(null);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const layoutRef = useRef<HTMLDivElement>(null);
  const messageEndRef = useRef<HTMLDivElement>(null);
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  const selectedKeyRef = useRef('');
  const messagesSessionKeyRef = useRef('');
  const messagesLengthRef = useRef(0);
  const shouldScrollMessagesRef = useRef(true);
  const forceScrollOnImageLoadRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const selectedSession = useMemo(
    () => sessions.find(session => getSessionKey(session) === selectedKey),
    [sessions, selectedKey]
  );
  const selectedBuyerDisplayName = getBuyerDisplayName(selectedSession);

  const getDisplayUnreadCount = useCallback(
    (session: ChatSession) => getSessionKey(session) === selectedKey ? 0 : Number(session.unread_count || 0),
    [selectedKey]
  );

  const isSessionPaused = useCallback(
    (session: ChatSession) => getPauseRemaining(session, pauseNow) > 0,
    [pauseNow]
  );

  const clearAttachment = useCallback(() => {
    setAttachment(null);
  }, []);

  const isNearMessageBottom = useCallback(() => {
    const container = messagesScrollRef.current;
    if (!container) return true;
    return container.scrollHeight - container.scrollTop - container.clientHeight < 120;
  }, []);

  const scrollToMessageBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    window.requestAnimationFrame(() => {
      messageEndRef.current?.scrollIntoView({ behavior, block: 'end' });
    });
  }, []);

  const scrollToMessageBottomAfterMedia = useCallback(() => {
    scrollToMessageBottom('smooth');
    window.setTimeout(() => scrollToMessageBottom('smooth'), 80);
    window.setTimeout(() => scrollToMessageBottom('smooth'), 260);
  }, [scrollToMessageBottom]);

  const replaceAttachment = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) {
      showToast({ type: 'warning', message: '请选择图片文件' });
      return;
    }
    setAttachment({
      file,
      previewUrl: URL.createObjectURL(file),
    });
    showToast({ type: 'info', message: '图片已添加，点击发送后才会发出' });
  }, []);

  useEffect(() => {
    return () => {
      if (attachment?.previewUrl) URL.revokeObjectURL(attachment.previewUrl);
    };
  }, [attachment?.previewUrl]);

  const filterCounts = useMemo(() => ({
    all: sessions.length,
    unread: sessions.filter(session => getDisplayUnreadCount(session) > 0).length,
    paused: sessions.filter(session => isSessionPaused(session)).length,
    online: sessions.filter(session => session.online).length,
  }), [sessions, getDisplayUnreadCount, isSessionPaused]);

  const filteredSessions = useMemo(() => {
    return sessions.filter(session => {
      if (statusFilter === 'unread') return getDisplayUnreadCount(session) > 0;
      if (statusFilter === 'paused') return isSessionPaused(session);
      if (statusFilter === 'online') return Boolean(session.online);
      return true;
    });
  }, [sessions, statusFilter, getDisplayUnreadCount, isSessionPaused]);

  const loadSessions = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) setLoadingSessions(true);
    try {
      const res = await getChatSessions({
        cookie_id: cookieFilter || undefined,
        q: query || undefined,
        limit: 200,
      });
      setSessions(res.sessions || []);
      setAccounts(res.accounts || []);

      const currentSelectedKey = selectedKeyRef.current;
      if (!currentSelectedKey && res.sessions?.length) {
        setSelectedKey(getSessionKey(res.sessions[0]));
      } else if (currentSelectedKey && !res.sessions?.some(session => getSessionKey(session) === currentSelectedKey)) {
        setSelectedKey(res.sessions?.length ? getSessionKey(res.sessions[0]) : '');
      }
    } catch (error) {
      showToast({ type: 'error', message: error instanceof Error ? error.message : '加载会话失败' });
    } finally {
      if (!options?.silent) setLoadingSessions(false);
    }
  }, [cookieFilter, query]);

  const loadMessages = useCallback(async (session: ChatSession, options?: { showLoading?: boolean }) => {
    const sessionKey = getSessionKey(session);
    const shouldScrollAfterLoad =
      messagesSessionKeyRef.current !== sessionKey ||
      messagesLengthRef.current === 0 ||
      isNearMessageBottom();
    if (options?.showLoading !== false) setLoadingMessages(true);
    try {
      const res = await getChatMessages(session.cookie_id, session.chat_id, 200);
      if (selectedKeyRef.current && selectedKeyRef.current !== sessionKey) return;
      shouldScrollMessagesRef.current = shouldScrollAfterLoad;
      messagesSessionKeyRef.current = sessionKey;
      setMessages(res.messages || []);
      if (res.session) {
        const readSession = { ...res.session, unread_count: 0 };
        setSessions(prev => prev.map(item => getSessionKey(item) === getSessionKey(readSession) ? readSession : item));
      }
      onUnreadChange?.();
    } catch (error) {
      showToast({ type: 'error', message: error instanceof Error ? error.message : '加载消息失败' });
    } finally {
      if (!selectedKeyRef.current || selectedKeyRef.current === sessionKey) {
        setLoadingMessages(false);
      }
    }
  }, [isNearMessageBottom, onUnreadChange]);

  const applySendResponse = useCallback((res: { data?: ChatMessage; session?: ChatSession }) => {
    if (res.data) {
      shouldScrollMessagesRef.current = true;
      messagesSessionKeyRef.current = getSessionKey(res.data);
      setMessages(prev => prev.some(item => item.id === res.data!.id) ? prev : [...prev, res.data!]);
    }
    if (res.session) {
      const currentSession = { ...res.session, unread_count: 0 };
      setSessions(prev => prev.map(item => getSessionKey(item) === getSessionKey(currentSession) ? currentSession : item));
    }
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadSessions();
    }, 250);
    return () => window.clearTimeout(timer);
  }, [loadSessions]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setPauseNow(Date.now());
    }, 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    selectedKeyRef.current = selectedKey;
  }, [selectedKey]);

  useEffect(() => {
    if (
      targetSessionKey &&
      targetSessionKey !== selectedKey &&
      sessions.some(session => getSessionKey(session) === targetSessionKey)
    ) {
      setSelectedKey(targetSessionKey);
    }
  }, [targetSessionKey, selectedKey, sessions]);

  useEffect(() => {
    messagesLengthRef.current = messages.length;
  }, [messages.length]);

  useEffect(() => {
    if (!filteredSessions.length) {
      if (selectedKey) setSelectedKey('');
      return;
    }
    if (!selectedKey || !filteredSessions.some(session => getSessionKey(session) === selectedKey)) {
      setSelectedKey(getSessionKey(filteredSessions[0]));
    }
  }, [filteredSessions, selectedKey]);

  useEffect(() => {
    setDraft('');
    clearAttachment();
  }, [selectedKey, clearAttachment]);

  useEffect(() => {
    if (selectedSession) {
      loadMessages(selectedSession);
    } else {
      messagesSessionKeyRef.current = '';
      setMessages([]);
    }
  }, [selectedSession?.cookie_id, selectedSession?.chat_id, loadMessages]);

  useEffect(() => {
    if (!shouldScrollMessagesRef.current) return;
    scrollToMessageBottom(messages.length ? 'smooth' : 'auto');
    shouldScrollMessagesRef.current = false;
  }, [messages, selectedKey, scrollToMessageBottom]);

  useEffect(() => {
    const disconnect = connectChatEvents(
      (event) => {
        if (event.type === 'ready' || event.type === 'ping') {
          return;
        }

        const session = event.session as ChatSession | undefined;
        const message = event.message as ChatMessage | undefined;

        if (session) {
          setSessions(prev => {
            const key = getSessionKey(session);
            const exists = prev.some(item => getSessionKey(item) === key);
            const nextSession = { ...session, unread_count: key === selectedKeyRef.current ? 0 : session.unread_count };
            const next = exists
              ? prev.map(item => getSessionKey(item) === key ? { ...item, ...nextSession } : item)
              : [nextSession, ...prev];
            return next.sort((a, b) => String(b.last_message_at || b.updated_at || '').localeCompare(String(a.last_message_at || a.updated_at || '')));
          });
        } else {
          loadSessions({ silent: true });
        }

        if (message && selectedKeyRef.current === getSessionKey(message)) {
          shouldScrollMessagesRef.current = isNearMessageBottom();
          messagesSessionKeyRef.current = getSessionKey(message);
          setMessages(prev => {
            if (prev.some(item => item.id === message.id)) return prev;
            return [...prev, message];
          });
          if (message.direction === 'in') {
            markChatRead(message.cookie_id, message.chat_id)
              .then(() => {
                setSessions(prev => prev.map(item => getSessionKey(item) === getSessionKey(message) ? { ...item, unread_count: 0 } : item));
                onUnreadChange?.();
              })
              .catch(() => undefined);
          }
        }
      },
      (error) => {
        showToast({ type: 'warning', message: error.message || '消息流已断开' });
      }
    );

    return disconnect;
  }, [isNearMessageBottom, loadSessions, onUnreadChange]);

  const canCompose = Boolean(selectedSession) && !sending;
  const canSend = Boolean(selectedSession?.online) && !sending;
  const hasDraft = Boolean(draft.trim());
  const hasAttachment = Boolean(attachment);

  const clampSessionListWidth = useCallback((width: number) => {
    const contextVisible = typeof window !== 'undefined' && window.innerWidth >= 1280;
    const contextWidth = selectedSession && contextVisible ? (contextCollapsed ? CONTEXT_COLLAPSED_WIDTH : CONTEXT_PANEL_WIDTH) : 0;
    const containerWidth = layoutRef.current?.clientWidth || (typeof window === 'undefined' ? 0 : window.innerWidth);
    const viewportLimit = typeof window === 'undefined'
      ? SESSION_LIST_MAX_WIDTH
      : containerWidth - MESSAGE_AREA_MIN_WIDTH - contextWidth;
    const maxWidth = Math.max(SESSION_LIST_MIN_WIDTH, Math.min(SESSION_LIST_MAX_WIDTH, viewportLimit));
    return Math.max(SESSION_LIST_MIN_WIDTH, Math.min(maxWidth, width));
  }, [contextCollapsed, selectedSession]);

  const startSessionListResize = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    setResizingSessionList(true);
  }, []);

  useEffect(() => {
    if (!resizingSessionList) return;

    const handlePointerMove = (event: PointerEvent) => {
      const layoutLeft = layoutRef.current?.getBoundingClientRect().left || 0;
      setSessionListWidth(clampSessionListWidth(event.clientX - layoutLeft));
    };

    const handlePointerUp = () => {
      setResizingSessionList(false);
    };

    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [clampSessionListWidth, resizingSessionList]);

  useEffect(() => {
    setSessionListWidth(width => clampSessionListWidth(width));
  }, [clampSessionListWidth]);

  const handleSend = async () => {
    if (!selectedSession || sending || (!hasDraft && !attachment)) return;
    if (!selectedSession.online) {
      showToast({ type: 'warning', message: '账号离线，当前无法发送' });
      return;
    }

    const text = draft.trim();
    const imageFile = attachment?.file;
    let imageSent = false;
    setSending(true);

    try {
      if (imageFile) {
        const imageRes = await sendChatImageMessage(
          selectedSession.cookie_id,
          selectedSession.chat_id,
          selectedSession.buyer_id,
          imageFile
        );
        imageSent = true;
        forceScrollOnImageLoadRef.current = true;
        clearAttachment();
        applySendResponse(imageRes);
      }

      if (text) {
        const textRes = await sendChatMessage(
          selectedSession.cookie_id,
          selectedSession.chat_id,
          selectedSession.buyer_id,
          text
        );
        setDraft('');
        applySendResponse(textRes);
      }

      if (imageFile) {
        scrollToMessageBottomAfterMedia();
      } else {
        scrollToMessageBottom('smooth');
      }
    } catch (error) {
      if (imageSent) clearAttachment();
      showToast({ type: 'error', message: error instanceof Error ? error.message : '发送失败' });
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const imageItem = Array.from(event.clipboardData.items).find(item => item.kind === 'file' && item.type.startsWith('image/'));
    const file = imageItem?.getAsFile();
    if (!file) return;
    event.preventDefault();
    replaceAttachment(file);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
    const file = Array.from(event.dataTransfer.files).find(item => item.type.startsWith('image/'));
    if (!file) {
      showToast({ type: 'warning', message: '拖拽内容中没有图片' });
      return;
    }
    replaceAttachment(file);
  };

  const filterOptions: Array<{ key: SessionFilter; label: string }> = [
    { key: 'all', label: '全部' },
    { key: 'unread', label: '未读' },
    { key: 'paused', label: '暂停' },
    { key: 'online', label: '在线' },
  ];

  const accountOptions = accounts.map(account => ({
    value: account.id,
    label: account.nickname || account.name || account.remark || account.username || account.id
  }));
  const accountFilterOptions = [{ value: '', label: '全部账号' }, ...accountOptions];

  const productImage = resolveAssetUrl(selectedSession?.item_image);
  const selectedPauseRemaining = getPauseRemaining(selectedSession, pauseNow);

  return (
    <div ref={layoutRef} className="flex h-full min-h-0 w-full overflow-hidden bg-[#F7F8FA] text-slate-900">
      <aside
        className="hidden h-full flex-none flex-col bg-white lg:flex"
        style={{ width: sessionListWidth }}
      >
        <div className="border-b border-slate-100 px-4 py-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-base font-semibold text-slate-900">
                <MessageCircle className="h-5 w-5 text-slate-700" />
                消息中心
              </div>
              <div className="mt-1 text-xs font-medium text-slate-500">{sessions.length} 个会话</div>
            </div>
            <button
              type="button"
              onClick={() => loadSessions()}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-slate-300 hover:text-slate-950"
              title="刷新会话"
            >
              {loadingSessions ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </button>
          </div>

          <div className="mt-4 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="搜索买家、商品、消息"
                className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm font-normal text-slate-900 outline-none transition placeholder:text-slate-500 focus:border-[#D8BD00] focus:bg-white"
              />
            </div>

            <Select
              value={cookieFilter}
              onChange={setCookieFilter}
              className="h-10 w-full border border-slate-200 bg-slate-50"
              size="sm"
              options={accountFilterOptions}
              placeholder="全部账号"
            />

            <div className="grid grid-cols-4 gap-1 rounded-xl bg-slate-50 p-1 ring-1 ring-slate-200">
              {filterOptions.map(option => {
                const active = statusFilter === option.key;
                return (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setStatusFilter(option.key)}
                    className={`h-8 min-w-0 rounded-lg px-1.5 text-[11px] font-medium transition ${
                      active ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:bg-white/70 hover:text-slate-800'
                    }`}
                    title={`${option.label} ${filterCounts[option.key]}`}
                  >
                    <span className="flex min-w-0 items-center justify-center gap-1">
                      <span className="truncate">{option.label}</span>
                      <span className={active ? 'text-[10px] text-[#8A7300]' : 'text-[10px] text-slate-400'}>
                        {filterCounts[option.key]}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
          {loadingSessions && filteredSessions.length === 0 ? (
            <div className="space-y-2 p-2">
              {Array.from({ length: 7 }).map((_, index) => (
                <div key={index} className="h-[82px] animate-pulse rounded-2xl bg-slate-100" />
              ))}
            </div>
          ) : filteredSessions.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center px-8 text-center text-slate-500">
              <Inbox className="mb-3 h-10 w-10 text-slate-300" />
              <div className="text-sm font-semibold text-slate-700">暂无匹配会话</div>
              <div className="mt-1 text-xs leading-5">换个筛选条件或等待新消息进入。</div>
            </div>
          ) : (
            filteredSessions.map(session => {
              const sessionKey = getSessionKey(session);
              const active = selectedKey === sessionKey;
              const unreadCount = getDisplayUnreadCount(session);
              const buyerDisplayName = getBuyerDisplayName(session);
              return (
                <button
                  key={sessionKey}
                  type="button"
                  onClick={() => {
                    setSelectedKey(sessionKey);
                    setSessions(prev => prev.map(item => getSessionKey(item) === sessionKey ? { ...item, unread_count: 0 } : item));
                  }}
                  className={`mb-1.5 w-full rounded-2xl px-3 py-3 text-left transition ${
                    active
                      ? 'bg-[#FFF8D8] text-slate-900 ring-1 ring-[#F1D565]'
                      : 'text-slate-900 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex min-w-0 items-start gap-3">
                    <Avatar
                      src={session.buyer_avatar}
                      name={buyerDisplayName}
                      className={`h-10 w-10 rounded-2xl ${active ? 'bg-white text-slate-800 ring-1 ring-[#F1D565]' : 'bg-slate-200 text-slate-700'}`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex min-w-0 items-center gap-2">
                            <span className="truncate text-sm font-semibold">{buyerDisplayName}</span>
                            {unreadCount > 0 && (
                              <span className="flex h-5 min-w-5 flex-none items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-semibold text-white">
                                {unreadCount}
                              </span>
                            )}
                          </div>
                          <div className={`mt-1 truncate text-xs font-normal ${active ? 'text-slate-600' : 'text-slate-500'}`}>
                            {getMessagePreview(session)}
                          </div>
                        </div>
                        <span className={`flex-none text-xs font-medium ${active ? 'text-[#8A7300]' : 'text-slate-400'}`}>
                          {formatTime(session.last_message_at)}
                        </span>
                      </div>
                      <div className={`mt-2 flex min-w-0 items-center gap-2 text-[11px] font-medium ${active ? 'text-slate-500' : 'text-slate-400'}`}>
                        <span className="truncate">{session.account_name || session.cookie_id}</span>
                        {isSessionPaused(session) && <PauseCircle className="h-3.5 w-3.5 flex-none text-amber-500" />}
                        {session.online ? (
                          <Wifi className="h-3.5 w-3.5 flex-none text-emerald-500" aria-label="账号在线" />
                        ) : (
                          <WifiOff className="h-3.5 w-3.5 flex-none" aria-label="账号离线" />
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </aside>

      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="调整会话列表宽度"
        title="拖动调整会话列表宽度"
        onPointerDown={startSessionListResize}
        className={`group hidden h-full w-2 flex-none cursor-col-resize items-stretch justify-center border-x border-transparent bg-white lg:flex ${
          resizingSessionList ? 'bg-[#FFF8D8]' : 'hover:bg-slate-50'
        }`}
      >
        <div className={`h-full w-px transition ${resizingSessionList ? 'bg-[#E4C400]' : 'bg-slate-200 group-hover:bg-slate-300'}`} />
      </div>

      <section className="flex h-full min-w-0 flex-1 flex-col lg:min-w-[560px]">
        {selectedSession ? (
          <>
            <header className="flex h-[72px] flex-none items-center justify-between gap-4 border-b border-slate-200 bg-white px-5">
              <div className="flex min-w-0 items-center gap-3">
                <Avatar
                  src={selectedSession.buyer_avatar}
                  name={selectedBuyerDisplayName}
                  className="h-11 w-11 rounded-2xl bg-slate-200"
                  textClassName="text-base"
                />
                <div className="min-w-0">
                  <div className="flex min-w-0 items-center gap-2">
                    <h2 className="truncate text-base font-semibold text-slate-900">
                      {selectedBuyerDisplayName}
                    </h2>
                    <span className={`inline-flex flex-none items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                      selectedSession.online ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {selectedSession.online ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
                      {selectedSession.online ? '账号在线' : '账号离线'}
                    </span>
                    {selectedPauseRemaining > 0 && (
                      <span className="hidden items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 md:inline-flex">
                        <PauseCircle className="h-3 w-3" />
                        暂停 {formatPause(selectedPauseRemaining)}
                      </span>
                    )}
                  </div>
                </div>
              </div>

            </header>

            <div ref={messagesScrollRef} className="min-h-0 flex-1 overflow-y-auto bg-[#F7F8FA] px-4 py-5">
              {loadingMessages && messages.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm font-medium text-slate-500">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  正在加载消息
                </div>
              ) : messages.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center text-center text-slate-500">
                  <MessageCircle className="mb-3 h-10 w-10 text-slate-300" />
                  <div className="text-sm font-semibold text-slate-700">这个会话还没有消息记录</div>
                  <div className="mt-1 text-xs">新消息会实时出现在这里。</div>
                </div>
              ) : (
                <div className="mx-auto flex w-full max-w-[920px] flex-col gap-4">
                  {messages.map(message => {
                    const isOutgoing = message.direction === 'out';
                    const label = sourceLabel(message.source);
                    return (
                      <div key={message.id} className={`flex items-end gap-3 ${isOutgoing ? 'justify-end' : 'justify-start'}`}>
                        {!isOutgoing && (
                          <Avatar
                            src={(message.metadata || {}).buyer_avatar || (message.metadata || {}).sender_avatar || selectedSession.buyer_avatar}
                            name={getMessageBuyerDisplayName(message, selectedSession)}
                            className="h-9 w-9 rounded-2xl bg-slate-200"
                          />
                        )}
                        <div className={`flex max-w-[78%] flex-col gap-1 ${isOutgoing ? 'items-end' : 'items-start'}`}>
                          <div className={`rounded-2xl px-4 py-3 shadow-sm ${
                            isOutgoing
                              ? message.send_status === 'failed'
                                ? 'bg-red-50 text-red-700 ring-1 ring-red-100'
                                : 'bg-[#FFE815] text-slate-900'
                              : 'bg-white text-slate-900 ring-1 ring-slate-100'
                          }`}>
                            <MessageBody
                              message={message}
                              onImageClick={(url, alt) => setLightbox({ url, alt })}
                              onImageLoad={() => {
                                if (forceScrollOnImageLoadRef.current || isNearMessageBottom()) {
                                  scrollToMessageBottomAfterMedia();
                                  forceScrollOnImageLoadRef.current = false;
                                }
                              }}
                            />
                          </div>
                          <div className={`flex items-center gap-2 px-1 text-[11px] font-medium text-slate-400 ${isOutgoing ? 'justify-end' : 'justify-start'}`}>
                            {label && <span>{label}</span>}
                            <span>{formatTime(message.created_at)}</span>
                            {message.send_status === 'sent' && isOutgoing && <CheckCircle2 className="h-3 w-3 text-emerald-500" />}
                            {message.send_status === 'failed' && (
                              <span className="inline-flex items-center gap-1 text-red-500">
                                <AlertCircle className="h-3 w-3" />
                                发送失败
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messageEndRef} />
                </div>
              )}
            </div>

            <div
              className={`flex-none border-t border-slate-200 bg-white/95 px-4 py-4 backdrop-blur ${
                dragActive ? 'ring-2 ring-inset ring-[#FFE815]' : ''
              }`}
              onDragEnter={(event) => {
                event.preventDefault();
                setDragActive(true);
              }}
              onDragOver={(event) => {
                event.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={(event) => {
                if (event.currentTarget === event.target) setDragActive(false);
              }}
              onDrop={handleDrop}
            >
              <div className="w-full min-w-0">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.currentTarget.files?.[0];
                    if (file) replaceAttachment(file);
                    event.currentTarget.value = '';
                  }}
                />

                <div
                  className={`rounded-[24px] border bg-slate-50 px-4 py-3 transition ${
                    dragActive ? 'border-[#D8BD00] bg-[#FFFCE6]' : 'border-slate-200 focus-within:border-[#D8BD00] focus-within:bg-white'
                  }`}
                >
                  {attachment && (
                    <div className="mb-2 flex h-16 w-full max-w-[360px] items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3 shadow-sm">
                      <img src={attachment.previewUrl} alt="待发送图片" className="h-12 w-12 flex-none rounded-xl object-cover" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-slate-900">{attachment.file.name || '粘贴图片'}</div>
                        <div className="mt-0.5 truncate text-xs font-medium text-slate-500">
                          图片待确认，点击发送后发出
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={clearAttachment}
                        className="flex h-7 w-7 flex-none items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
                        title="移除图片"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}

                  <textarea
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    rows={2}
                    placeholder={selectedSession.online ? '输入回复内容，Enter 发送，Shift+Enter 换行' : '账号离线，可先编辑草稿，在线后发送'}
                    disabled={sending}
                    className="min-h-[52px] w-full resize-none bg-transparent px-1 py-1 text-sm leading-6 text-slate-900 outline-none placeholder:text-slate-500 disabled:cursor-not-allowed disabled:text-slate-400"
                  />

                  <div className="mt-2 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={!canCompose}
                        className="flex h-8 w-8 items-center justify-center rounded-full text-slate-600 transition hover:bg-white hover:text-slate-900 disabled:cursor-not-allowed disabled:text-slate-300"
                        title="上传图片"
                      >
                        <ImagePlus className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => showToast({ type: 'info', message: '语音发送能力待接入' })}
                        disabled={!canCompose}
                        className="flex h-8 w-8 items-center justify-center rounded-full text-slate-600 transition hover:bg-white hover:text-slate-900 disabled:cursor-not-allowed disabled:text-slate-300"
                        title="发送语音"
                      >
                        <Mic className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => showToast({ type: 'info', message: '视频通话能力待接入' })}
                        disabled={!canCompose}
                        className="flex h-8 w-8 items-center justify-center rounded-full text-slate-600 transition hover:bg-white hover:text-slate-900 disabled:cursor-not-allowed disabled:text-slate-300"
                        title="视频通话"
                      >
                        <Video className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => showToast({ type: 'info', message: '更多快捷操作待接入' })}
                        disabled={!canCompose}
                        className="flex h-8 w-8 items-center justify-center rounded-full text-slate-600 transition hover:bg-white hover:text-slate-900 disabled:cursor-not-allowed disabled:text-slate-300"
                        title="更多操作"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="flex min-w-0 items-center gap-3">
                      <div className="hidden min-w-0 items-center gap-1 truncate text-xs font-medium text-slate-500 sm:flex">
                        <Paperclip className="h-3.5 w-3.5 flex-none" />
                        支持拖拽或粘贴图片后确认发送
                      </div>
                      <button
                        type="button"
                        onClick={handleSend}
                        disabled={!canSend || (!hasDraft && !hasAttachment)}
                        className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-[#FFE815] text-slate-900 transition hover:bg-[#F4D700] disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
                        title="发送"
                      >
                        {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex h-full flex-col items-center justify-center text-center text-slate-500">
            <Inbox className="mb-3 h-12 w-12 text-slate-300" />
            <div className="text-base font-semibold text-slate-800">选择一个会话开始处理</div>
            <div className="mt-1 text-sm">会话列表会实时显示买家新消息。</div>
          </div>
        )}
      </section>

      {selectedSession && (
        contextCollapsed ? (
          <aside className="hidden h-full w-12 flex-none flex-col items-center border-l border-slate-200 bg-white py-4 xl:flex">
            <button
              type="button"
              onClick={() => setContextCollapsed(false)}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:border-slate-300 hover:text-slate-950"
              title="展开上下文面板"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          </aside>
        ) : (
          <aside className="hidden h-full w-[306px] flex-none flex-col border-l border-slate-200 bg-white xl:flex">
            <div className="flex h-[72px] items-center justify-between border-b border-slate-100 px-4">
              <div>
                <div className="text-sm font-semibold text-slate-900">商品信息</div>
                <div className="mt-1 text-xs font-medium text-slate-500">买家、商品与会话状态</div>
              </div>
              <button
                type="button"
                onClick={() => setContextCollapsed(true)}
                className="flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-950"
                title="折叠上下文面板"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
              <section className="border-b border-slate-100 pb-4">
                <div className="flex items-center gap-3">
                  <Avatar
                    src={selectedSession.buyer_avatar}
                    name={selectedBuyerDisplayName}
                    className="h-12 w-12 rounded-2xl bg-slate-200"
                    textClassName="text-base"
                  />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-slate-900">{selectedBuyerDisplayName}</div>
                    <div className="mt-1 flex items-center gap-1 text-xs font-medium text-slate-500">
                      {selectedSession.online ? <Wifi className="h-3.5 w-3.5 text-emerald-500" /> : <WifiOff className="h-3.5 w-3.5 text-slate-400" />}
                      {selectedSession.online ? '账号在线' : '账号离线'}
                    </div>
                  </div>
                </div>
              </section>

              <section className="border-b border-slate-100 py-4">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-900">关联商品</h3>
                  <Package className="h-4 w-4 text-slate-400" />
                </div>
                <button
                  type="button"
                  onClick={() => setProductModalOpen(true)}
                  className="group w-full overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 text-left transition hover:border-slate-300 hover:bg-white"
                >
                  <div className="relative aspect-[4/3] bg-slate-100">
                    {productImage ? (
                      <img src={productImage} alt={selectedSession.item_title || '商品图片'} className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full flex-col items-center justify-center text-slate-400">
                        <Package className="mb-2 h-8 w-8" />
                        <span className="text-xs font-medium">暂无商品图</span>
                      </div>
                    )}
                    <div className="absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-full bg-slate-700/70 text-white opacity-0 transition group-hover:opacity-100">
                      <Maximize2 className="h-4 w-4" />
                    </div>
                  </div>
                  <div className="px-3 py-3">
                    <div className="line-clamp-2 text-sm font-medium leading-5 text-slate-900">
                      {selectedSession.item_title || '未获取到商品标题'}
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-2 text-xs font-semibold text-slate-500">
                      <span className="truncate">{selectedSession.item_id || '暂无商品 ID'}</span>
                      {selectedSession.item_price && <span className="flex-none text-slate-900">{formatItemPrice(selectedSession.item_price)}</span>}
                    </div>
                  </div>
                </button>
              </section>

              <section className="border-b border-slate-100 py-4">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-900">会话状态</h3>
                  <Clock3 className="h-4 w-4 text-slate-400" />
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-semibold text-slate-500">自动回复</span>
                    <span className={`font-semibold ${selectedPauseRemaining > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                      {selectedPauseRemaining > 0 ? `暂停 ${formatPause(selectedPauseRemaining)}` : '运行中'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-semibold text-slate-500">未读</span>
                    <span className="font-semibold text-slate-900">{getDisplayUnreadCount(selectedSession)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-semibold text-slate-500">最近消息</span>
                    <span className="font-semibold text-slate-900">{formatFullTime(selectedSession.last_message_at)}</span>
                  </div>
                </div>
              </section>

              <section className="py-4">
                <div className="mb-3 text-sm font-semibold text-slate-900">技术信息</div>
                <div className="space-y-2 text-xs font-semibold text-slate-500">
                  <div className="break-all rounded-2xl bg-slate-50 px-3 py-2">Chat ID: {selectedSession.chat_id}</div>
                  <div className="break-all rounded-2xl bg-slate-50 px-3 py-2">买家 ID: {selectedSession.buyer_id || '-'}</div>
                  <div className="break-all rounded-2xl bg-slate-50 px-3 py-2">账号: {selectedSession.account_name || selectedSession.cookie_id}</div>
                </div>
              </section>
            </div>
          </aside>
        )
      )}

      {lightbox && <ImageLightbox image={lightbox} onClose={() => setLightbox(null)} />}
      {productModalOpen && selectedSession && <ProductDetailModal session={selectedSession} onClose={() => setProductModalOpen(false)} />}
    </div>
  );
};

export default MessageCenter;
